import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { Permission } from '@/config/permissions';
import { logPermissionDeniedDebounced } from '@/services/audit';
import { notifyPermissionDeniedDebounced } from '@/services/events/permissionDeniedNotify';
import { routePaths } from '@/routes/paths';
import { useAuth } from '@/shared/hooks/useAuth';

type RequirePermissionProps = {
  permission: Permission;
  children: ReactNode;
  fallback?: ReactNode;
};

export function RequirePermission({
  permission,
  children,
  fallback,
}: RequirePermissionProps) {
  const { hasPermission, loading, profile } = useAuth();
  const location = useLocation();

  if (loading) {
    return <p className="auth-muted">Cargando…</p>;
  }

  if (!hasPermission(permission)) {
    logPermissionDeniedDebounced(`permission:${permission}`, {
      entityType: 'system',
      entityId: permission,
      metadata: {
        permission,
        path: location.pathname,
        role: profile?.role,
      },
    });
    notifyPermissionDeniedDebounced(`permission:${permission}`, {
      permission,
      path: location.pathname,
    });
    if (fallback) return <>{fallback}</>;
    return <Navigate to={routePaths.accessDenied} replace />;
  }

  return <>{children}</>;
}
